import { PopulationSamples } from '../shared/model/population';

/**
 * Customer demographic profile — a capture-rate matrix over (sex × age band).
 *
 * `weights[sex][band]` ∈ [0..1] is the share of that group that counts as a
 * target customer ("0.75 for men 20–25" → every 4th man aged 20–25 is a
 * customer). It is NOT a distribution: cells are independent and don't sum to
 * anything. Applying the profile to real demographics is a scalar multiply —
 * see {@link estimateAudience}.
 *
 * The profile is a piecewise-constant function over age. Its boundaries are
 * shared by both sexes (so the state is a rectangular matrix) and are
 * independent of the census buckets: {@link estimateAudience} integrates the
 * profile over each data bucket assuming a uniform age distribution inside
 * the bucket, so a "20–22" band works even though the data comes in "20–24".
 *
 * This module is pure TypeScript — no Angular, no HTTP, no map imports — so
 * the same model can back any host (sales-forecast pages, the map, reports).
 */

export type ProfileSex = 'F' | 'M';

export const PROFILE_SEXES: readonly ProfileSex[] = ['F', 'M'];

/** Ages at or above TOP_AGE are treated as a single open-ended "85+" group. */
export const TOP_AGE = 85;

/** Exclusive upper bound of the last possible band ([85, 86) renders "85+"). */
export const MAX_BREAK = TOP_AGE + 1;

export interface CustomerProfile {
  version: 1;
  /**
   * Ascending age boundaries; first is always 0, last is always MAX_BREAK.
   * Defines ageBreaks.length - 1 bands: band i is [ageBreaks[i], ageBreaks[i+1]).
   */
  ageBreaks: number[];
  /** Per sex, one capture rate (0..1) per band. */
  weights: Record<ProfileSex, number[]>;
}

/**
 * The state simple mode can express: one contiguous age run and a
 * women↔men balance. balance ∈ [-1..1]: -1 women only, 0 everyone,
 * +1 men only. The dominant sex always stays at 100%; the balance scales
 * the other sex down — see {@link sexWeightsForBalance}.
 */
export interface SimpleSelection {
  /** Inclusive lower age bound of the selection. */
  ageFrom: number;
  /** Exclusive upper age bound; MAX_BREAK means "85+ included". */
  ageTo: number;
  balance: number;
}

export const EVERYONE_SELECTION: SimpleSelection = {
  ageFrom: 0,
  ageTo: MAX_BREAK,
  balance: 0,
};

export interface AudienceEstimate {
  /** People matching the profile (already weighted). */
  target: number;
  /** All residents in the samples. */
  total: number;
}

const WEIGHT_EPSILON = 1e-6;

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

export function sexWeightsForBalance(
  balance: number
): Record<ProfileSex, number> {
  return {
    F: balance <= 0 ? 1 : round2(1 - balance),
    M: balance >= 0 ? 1 : round2(1 + balance),
  };
}

export function fromSimple(sel: SimpleSelection): CustomerProfile {
  const breaks = [
    ...new Set(
      [0, sel.ageFrom, sel.ageTo, MAX_BREAK]
        .map((age) => Math.min(Math.max(Math.round(age), 0), MAX_BREAK))
        .sort((a, b) => a - b)
    ),
  ];
  const sexWeights = sexWeightsForBalance(sel.balance);
  const weights = {} as Record<ProfileSex, number[]>;

  for (const sex of PROFILE_SEXES) {
    weights[sex] = [];
    for (let i = 0; i < breaks.length - 1; i++) {
      const inside = breaks[i] >= sel.ageFrom && breaks[i + 1] <= sel.ageTo;
      weights[sex].push(inside ? sexWeights[sex] : 0);
    }
  }

  return { version: 1, ageBreaks: breaks, weights };
}

export function everyoneProfile(): CustomerProfile {
  return fromSimple(EVERYONE_SELECTION);
}

/**
 * Inverse of {@link fromSimple} where possible. Returns null when the profile
 * uses per-band fine-tuning that simple mode cannot express: a non-contiguous
 * age run, different weights across bands of the same sex, or no sex at 100%.
 */
export function toSimple(profile: CustomerProfile): SimpleSelection | null {
  const bandCount = profile.ageBreaks.length - 1;
  const nonZero: number[] = [];

  for (let i = 0; i < bandCount; i++) {
    if (
      profile.weights.F[i] > WEIGHT_EPSILON ||
      profile.weights.M[i] > WEIGHT_EPSILON
    ) {
      nonZero.push(i);
    }
  }

  if (nonZero.length === 0) return null;

  const first = nonZero[0];
  const last = nonZero[nonZero.length - 1];
  if (last - first + 1 !== nonZero.length) return null; // non-contiguous

  const wF = profile.weights.F[first];
  const wM = profile.weights.M[first];
  for (let i = first; i <= last; i++) {
    if (
      Math.abs(profile.weights.F[i] - wF) > WEIGHT_EPSILON ||
      Math.abs(profile.weights.M[i] - wM) > WEIGHT_EPSILON
    ) {
      return null; // not uniform inside the run
    }
  }

  if (Math.abs(Math.max(wF, wM) - 1) > WEIGHT_EPSILON) return null;

  const balance = wF >= 1 - WEIGHT_EPSILON ? round2(wM - 1) : round2(1 - wF);

  return {
    ageFrom: profile.ageBreaks[first],
    ageTo: profile.ageBreaks[last + 1],
    balance,
  };
}

/**
 * The nearest {@link SimpleSelection} for any profile — how the user
 * "retreats" from fine-tuning back to the simple controls. Exact when the
 * profile is simple-representable; otherwise approximates: the age span of
 * all non-zero bands, each sex's width-weighted average weight over that
 * span, scaled so the dominant sex is back at 100% (the invariant simple
 * mode requires).
 */
export function closestSimple(profile: CustomerProfile): SimpleSelection {
  const exact = toSimple(profile);
  if (exact) return exact;

  const bandCount = profile.ageBreaks.length - 1;
  let first = -1;
  let last = -1;
  for (let i = 0; i < bandCount; i++) {
    if (
      profile.weights.F[i] > WEIGHT_EPSILON ||
      profile.weights.M[i] > WEIGHT_EPSILON
    ) {
      if (first < 0) first = i;
      last = i;
    }
  }
  if (first < 0) return EVERYONE_SELECTION; // nobody selected

  const runWidth = profile.ageBreaks[last + 1] - profile.ageBreaks[first];
  const average = (sex: ProfileSex): number => {
    let weighted = 0;
    for (let i = first; i <= last; i++) {
      const width = profile.ageBreaks[i + 1] - profile.ageBreaks[i];
      weighted += width * profile.weights[sex][i];
    }
    return weighted / runWidth;
  };

  const avgF = average('F');
  const avgM = average('M');
  const dominant = Math.max(avgF, avgM);
  const balance =
    avgF >= avgM ? round2(avgM / dominant - 1) : round2(1 - avgF / dominant);

  return {
    ageFrom: profile.ageBreaks[first],
    ageTo: profile.ageBreaks[last + 1],
    balance,
  };
}

export function isEveryone(profile: CustomerProfile): boolean {
  return PROFILE_SEXES.every((sex) =>
    profile.weights[sex].every((w) => Math.abs(w - 1) < WEIGHT_EPSILON)
  );
}

export function setWeight(
  profile: CustomerProfile,
  sex: ProfileSex,
  bandIndex: number,
  weight: number
): CustomerProfile {
  const weights = {
    F: [...profile.weights.F],
    M: [...profile.weights.M],
  };
  weights[sex][bandIndex] = round2(clamp01(weight));
  return { ...profile, weights };
}

/**
 * The fixed band grid the pyramid editor works on: finer where customer
 * profiles change fastest (5-year steps through the prime 15–40 range),
 * coarser at the extremes. The last band is the open-ended "70+".
 */
export const PYRAMID_AGE_BREAKS: readonly number[] = [
  0,
  15,
  20,
  25,
  30,
  35,
  40,
  50,
  60,
  70,
  MAX_BREAK,
];

/**
 * Re-expresses the profile on the given band grid using exact width-weighted
 * averages (the profile is piecewise-constant, so averaging is lossless when
 * the old boundaries lie on the new grid, and the honest approximation when
 * they don't — e.g. a slider-made "18–35" shows band 15–20 at 40%).
 */
export function regrid(
  profile: CustomerProfile,
  ageBreaks: readonly number[]
): CustomerProfile {
  const alreadyOnGrid =
    profile.ageBreaks.length === ageBreaks.length &&
    profile.ageBreaks.every((boundary, i) => boundary === ageBreaks[i]);
  if (alreadyOnGrid) return profile;

  const weights = {} as Record<ProfileSex, number[]>;
  for (const sex of PROFILE_SEXES) {
    weights[sex] = [];
    for (let band = 0; band < ageBreaks.length - 1; band++) {
      const from = ageBreaks[band];
      const to = ageBreaks[band + 1];
      let weighted = 0;
      for (let j = 0; j < profile.ageBreaks.length - 1; j++) {
        const overlap =
          Math.min(to, profile.ageBreaks[j + 1]) -
          Math.max(from, profile.ageBreaks[j]);
        if (overlap > 0) weighted += overlap * profile.weights[sex][j];
      }
      weights[sex].push(weighted / (to - from));
    }
  }

  return { ...profile, ageBreaks: [...ageBreaks], weights };
}

/**
 * Caps a data sample's age range into [0, MAX_BREAK) so open-ended top
 * buckets ("85+", ageTo 200 or MAX) land entirely inside the last band.
 */
function cappedSampleRange(
  fromInclusive: number,
  toExclusive: number
): [number, number] {
  const from = Math.min(fromInclusive, MAX_BREAK - 1);
  const to = Math.max(Math.min(toExclusive, MAX_BREAK), from + 1);
  return [from, to];
}

/**
 * The scalar multiply: Σ over samples of
 * population × overlapFraction(sample bucket, band) × weight[sex][band].
 */
export function estimateAudience(
  profile: CustomerProfile,
  samples: PopulationSamples
): AudienceEstimate {
  let total = 0;
  let target = 0;

  for (const sample of samples.getSamples()) {
    const sex = sample.sex as ProfileSex;
    const weights = profile.weights[sex];
    if (!weights) continue;

    const [from, to] = cappedSampleRange(
      sample.ageRange.fromInclusive,
      sample.ageRange.toExclusive
    );
    const width = to - from;
    total += sample.population;

    for (let band = 0; band < weights.length; band++) {
      if (weights[band] < WEIGHT_EPSILON) continue;
      const overlap =
        Math.min(to, profile.ageBreaks[band + 1]) -
        Math.max(from, profile.ageBreaks[band]);
      if (overlap > 0) {
        target += sample.population * (overlap / width) * weights[band];
      }
    }
  }

  return { target, total };
}

/** Unweighted population per (sex × band) — the pyramid's bar lengths. */
export function bandPopulations(
  profile: CustomerProfile,
  samples: PopulationSamples
): Record<ProfileSex, number[]> {
  const bandCount = profile.ageBreaks.length - 1;
  const result: Record<ProfileSex, number[]> = {
    F: new Array(bandCount).fill(0),
    M: new Array(bandCount).fill(0),
  };

  for (const sample of samples.getSamples()) {
    const sex = sample.sex as ProfileSex;
    if (!result[sex]) continue;

    const [from, to] = cappedSampleRange(
      sample.ageRange.fromInclusive,
      sample.ageRange.toExclusive
    );
    const width = to - from;

    for (let band = 0; band < bandCount; band++) {
      const overlap =
        Math.min(to, profile.ageBreaks[band + 1]) -
        Math.max(from, profile.ageBreaks[band]);
      if (overlap > 0) {
        result[sex][band] += sample.population * (overlap / width);
      }
    }
  }

  return result;
}

/**
 * Band label with shared boundary numbers ("20–25", "25–30", "85+") — reads
 * better for non-experts than census-style inclusive labels ("20–24").
 */
export function bandLabel(fromInclusive: number, toExclusive: number): string {
  return toExclusive >= MAX_BREAK
    ? `${fromInclusive}+`
    : `${fromInclusive}–${toExclusive}`;
}

function ageRunLabel(ageFrom: number, ageTo: number): string {
  if (ageFrom === 0 && ageTo >= MAX_BREAK) return '';
  return `aged ${bandLabel(ageFrom, ageTo)}`;
}

/**
 * One human-readable sentence for checkout payloads, analytics, and the
 * "custom profile" banner. Examples: "Everyone", "Women aged 18–35",
 * "All women and 25% of men, aged 20–45",
 * "Women: 20–25 100%, 25–30 50% · Men: 20–25 20%".
 */
export function profileSummary(profile: CustomerProfile): string {
  const simple = toSimple(profile);

  if (simple) {
    const age = ageRunLabel(simple.ageFrom, simple.ageTo);
    const w = sexWeightsForBalance(simple.balance);
    if (w.F === 1 && w.M === 1) return age ? `Everyone ${age}` : 'Everyone';
    if (w.M === 0) return age ? `Women ${age}` : 'Women';
    if (w.F === 0) return age ? `Men ${age}` : 'Men';
    const [main, other, share] =
      w.F === 1 ? ['women', 'men', w.M] : ['men', 'women', w.F];
    const base = `All ${main} and ${Math.round(share * 100)}% of ${other}`;
    return age ? `${base}, ${age}` : base;
  }

  const parts: string[] = [];
  for (const sex of PROFILE_SEXES) {
    const cells: string[] = [];
    for (let i = 0; i < profile.ageBreaks.length - 1; i++) {
      const weight = profile.weights[sex][i];
      if (weight > WEIGHT_EPSILON) {
        cells.push(
          `${bandLabel(profile.ageBreaks[i], profile.ageBreaks[i + 1])} ` +
            `${Math.round(weight * 100)}%`
        );
      }
    }
    if (cells.length > 0) {
      parts.push(`${sex === 'F' ? 'Women' : 'Men'}: ${cells.join(', ')}`);
    }
  }

  return parts.length > 0 ? parts.join(' · ') : 'No one selected';
}
